/**
 * ConvertMode V2
 *
 * Bildformat-Konvertierung mit:
 * - Upload oder Workspace-Auswahl
 * - Formatauswahl mit Quality-Slider
 * - Batch-Verarbeitung (max 10 Bilder)
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { RefreshCw, Download, Check, Image as ImageIcon } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, EmptyState, Loading, Badge } from '@/components/v2/ui';
import { InputSourceToggle, UploadArea, type InputSource } from '@/components/v2/tools/shared';
import { ImageGallery } from '@/components/v2/tools/gallery';
import { ImageSizeComparison } from '@/components/public/converter/ImageSizeComparison';
import { useWorkspaceImages } from '@/hooks/useWorkspaceImages';
import {
  convertImages,
  downloadConvertedImage,
  downloadAllConvertedImages,
  formatFileSize,
  FORMAT_INFO,
  FORMATS_WITH_QUALITY,
  type ImageFormat,
  type ConversionResultItem,
} from '@/lib/api/conversion';
import { getImageDimensions } from '@/lib/api/publicConverterApi';
import { cn } from '@/lib/utils';

type ConvertState = 'idle' | 'converting' | 'done';

const FORMATS: ImageFormat[] = ['png', 'jpeg', 'webp', 'avif', 'bmp'];

interface FileWithMetadata {
  file: File;
  width: number;
  height: number;
  size: number;
}

export function ConvertMode() {
  const { t } = useTranslation();
  // Input State
  const [inputSource, setInputSource] = useState<InputSource>('upload');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filesMetadata, setFilesMetadata] = useState<FileWithMetadata[]>([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  // Settings State
  const [targetFormat, setTargetFormat] = useState<ImageFormat>('webp');
  const [quality, setQuality] = useState(90);

  // Processing State
  const [state, setState] = useState<ConvertState>('idle');
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<ConversionResultItem[]>([]);

  // Workspace Images Query
  const { images, pagination, isLoading, isFetching } = useWorkspaceImages({
    page: currentPage,
    perPage: 12,
    enabled: inputSource === 'workspace',
  });

  // Handlers
  const handleSourceChange = (source: InputSource) => {
    setInputSource(source);
    setSelectedFiles([]);
    setFilesMetadata([]);
    setSelectedAssetIds(new Set());
    setResults([]);
    setState('idle');
  };

  const handleFilesChange = async (files: File[]) => {
    const limitedFiles = files.slice(0, 10);
    setSelectedFiles(limitedFiles);

    // Extract metadata
    const metadata = await Promise.all(
      limitedFiles.map(async (file) => {
        try {
          const dimensions = await getImageDimensions(file);
          return {
            file,
            width: dimensions.width,
            height: dimensions.height,
            size: file.size
          };
        } catch (error) {
          console.warn(`Failed to extract dimensions for ${file.name}:`, error);
          return {
            file,
            width: 0,
            height: 0,
            size: file.size
          };
        }
      })
    );

    setFilesMetadata(metadata);
    setResults([]);
    setState('idle');
  };

  const handleSelectionChange = useCallback(async (ids: Set<string>) => {
    const limitedIds = new Set(Array.from(ids).slice(0, 10));
    setSelectedAssetIds(limitedIds);
    setResults([]);
    setState('idle');

    // Extract metadata for first selected workspace image
    if (limitedIds.size > 0 && images) {
      const firstSelectedId = Array.from(limitedIds)[0];
      const firstImage = images.find((img) => img.id === firstSelectedId);

      if (firstImage) {
        try {
          const response = await fetch(`/image/${firstImage.id}`);
          const blob = await response.blob();
          const file = new File([blob], `image_${firstImage.id}.png`, { type: blob.type });

          const dimensions = await getImageDimensions(file);
          setFilesMetadata([{
            file,
            width: dimensions.width,
            height: dimensions.height,
            size: blob.size
          }]);
        } catch (error) {
          console.warn('Failed to extract metadata for workspace image:', error);
          setFilesMetadata([]);
        }
      }
    } else {
      setFilesMetadata([]);
    }
  }, [images]);

  const handleConvert = async () => {
    setState('converting');
    setProgress(0);
    setResults([]);

    try {
      let filesToConvert: File[] = [];

      if (inputSource === 'upload') {
        filesToConvert = selectedFiles;
      } else {
        // Download workspace images as files
        const selectedImages = images?.filter((img) => selectedAssetIds.has(img.id)) || [];
        for (const img of selectedImages) {
          try {
            const response = await fetch(`/image/${img.id}`);
            const blob = await response.blob();
            const file = new File([blob], `image_${img.id}.png`, { type: blob.type });
            filesToConvert.push(file);
          } catch {
            console.error(`Failed to download image ${img.id}`);
          }
        }
      }

      if (filesToConvert.length === 0) {
        throw new Error(t('editor.selectImages'));
      }

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + Math.random() * 10, 90));
      }, 300);

      const response = await convertImages(
        filesToConvert,
        targetFormat,
        FORMATS_WITH_QUALITY.includes(targetFormat) ? quality : undefined
      );

      clearInterval(progressInterval);
      setProgress(100);

      // Process results
      if (response.images) {
        setResults(response.images);
      } else if (response.image) {
        setResults([
          {
            success: response.success,
            filename: response.filename || 'converted',
            original_filename: response.original_filename,
            image: response.image,
            original_size: response.original_size,
            converted_size: response.converted_size,
            original_format: response.original_format,
            converted_format: response.converted_format,
          },
        ]);
      }

      setState('done');
      toast.success(t('toast.success.conversionComplete'));
    } catch (error) {
      setState('idle');
      setProgress(0);
      toast.error(error instanceof Error ? error.message : 'Konvertierung fehlgeschlagen');
    }
  };

  const handleDownloadAll = () => {
    downloadAllConvertedImages(results, targetFormat);
  };

  const inputCount = inputSource === 'upload' ? selectedFiles.length : selectedAssetIds.size;
  const isConverting = state === 'converting';
  const canConvert = inputCount > 0 && (state === 'idle' || state === 'done');
  const successCount = results.filter((r) => r.success).length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg shadow-orange-500/30">
          <RefreshCw className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white tracking-tight">
            {t('editor.convertTitle')}
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Konvertieren Sie Bilder zwischen verschiedenen Formaten
          </p>
        </div>
      </div>

      {/* Input View - Always visible */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Section */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="rounded-xl shadow-sm border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center gap-3 px-6 py-5 border-b border-neutral-200 dark:border-neutral-800">
                <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 border-2 border-orange-500 rounded-lg flex items-center justify-center font-bold text-sm text-orange-600 dark:text-orange-400">
                  1
                </div>
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white tracking-tight">
                  {t('editor.selectImages')}
                </h2>
              </div>
              <CardContent className="p-6 space-y-4">
                <InputSourceToggle value={inputSource} onChange={handleSourceChange} />

                {inputSource === 'upload' ? (
                  <UploadArea
                    files={selectedFiles}
                    onFilesChange={handleFilesChange}
                    maxFiles={10}
                    acceptedFormats={['image/png', 'image/jpeg', 'image/bmp', 'image/webp', 'image/avif']}
                    disabled={state === 'converting'}
                  />
                ) : (
                  <>
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loading size="lg" />
                      </div>
                    ) : images && images.length > 0 ? (
                      <ImageGallery
                        images={images}
                        isLoading={isLoading}
                        isFetching={isFetching}
                        pagination={pagination}
                        onPageChange={setCurrentPage}
                        selectedIds={selectedAssetIds}
                        onSelectionChange={handleSelectionChange}
                      />
                    ) : (
                      <EmptyState
                        icon={<ImageIcon className="h-12 w-12" />}
                        title={t('gallery.noImages')}
                        description="Es sind noch keine Bilder im Workspace vorhanden."
                      />
                    )}
                  </>
                )}

              </CardContent>
            </Card>

            {/* Image Size Comparison - Outside card for visibility */}
            {filesMetadata.length > 0 && filesMetadata[0].width > 0 && (
              <ImageSizeComparison
                original={{
                  width: filesMetadata[0].width,
                  height: filesMetadata[0].height,
                  fileSize: filesMetadata[0].size
                }}
                converted={
                  state === 'done' && results.length > 0 && results[0].success
                    ? {
                        width: filesMetadata[0].width, // Backend doesn't provide dimensions yet
                        height: filesMetadata[0].height,
                        fileSize: results[0].converted_size || filesMetadata[0].size
                      }
                    : undefined
                }
              />
            )}
          </div>

          {/* Settings Section */}
          <div className="space-y-4">
            <Card className="rounded-xl shadow-sm border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center gap-3 px-6 py-5 border-b border-neutral-200 dark:border-neutral-800">
                <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 border-2 border-orange-500 rounded-lg flex items-center justify-center font-bold text-sm text-orange-600 dark:text-orange-400">
                  2
                </div>
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white tracking-tight">
                  {t('editor.selectFormat')}
                </h2>
              </div>
              <CardContent className="p-6 space-y-6">
                {/* Format Selection */}
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                    {t('editor.selectFormat')}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {FORMATS.map((format) => (
                      <button
                        key={format}
                        onClick={() => setTargetFormat(format)}
                        disabled={state === 'converting'}
                        className={cn(
                          'p-4 rounded-xl border-2 text-left transition-all duration-200',
                          'hover:shadow-md hover:-translate-y-0.5',
                          targetFormat === format
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 shadow-lg shadow-orange-500/20'
                            : 'border-neutral-200 dark:border-neutral-700 hover:border-orange-300 dark:hover:border-orange-700 bg-white dark:bg-neutral-900'
                        )}
                      >
                        <p className="font-bold text-sm uppercase tracking-wider text-neutral-900 dark:text-neutral-100">
                          {FORMAT_INFO[format].name}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 leading-tight">
                          {FORMAT_INFO[format].info}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quality Slider */}
                {FORMATS_WITH_QUALITY.includes(targetFormat) && (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <label className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                        {t('editor.qualityLabel')}
                      </label>
                      <span className="text-xl font-bold text-orange-600 dark:text-orange-400">
                        {quality}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="100"
                      value={quality}
                      onChange={(e) => setQuality(Number(e.target.value))}
                      disabled={state === 'converting'}
                      className="w-full h-2 rounded-full appearance-none cursor-pointer bg-neutral-200 dark:bg-neutral-700 accent-orange-500"
                    />
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center">
                      {t('editor.qualityHint')}
                    </p>
                  </div>
                )}

                {/* Progress */}
                {state === 'converting' && (
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-neutral-700 dark:text-neutral-200">{t('common.processing')}</span>
                      <span className="text-orange-600 dark:text-orange-400">{Math.round(progress)}%</span>
                    </div>
                    <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden shadow-inner">
                      <div
                        className="h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-300 shadow-lg"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Convert Button */}
                <Button
                  className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 transition-all duration-200 hover:-translate-y-0.5"
                  onClick={handleConvert}
                  disabled={!canConvert || isConverting}
                >
                  {isConverting ? (
                    <>
                      <div className="h-5 w-5 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      <span className="font-semibold">{t('common.processing')}</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-5 w-5 mr-2" />
                      <span className="font-semibold">
                        {inputCount > 0
                          ? `${inputCount} ${inputCount === 1 ? t('common.image') : t('common.images')} konvertieren`
                          : t('editor.selectImages')}
                      </span>
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

      {/* Results View - Shown below when done */}
      {state === 'done' && results.length > 0 && (
        <Card className="rounded-xl shadow-sm border-neutral-200 dark:border-neutral-800">
          <CardHeader className="flex flex-row items-center justify-between px-6 py-5 border-b border-neutral-200 dark:border-neutral-800">
            <CardTitle className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <span className="text-lg font-semibold text-neutral-900 dark:text-white">
                {successCount} von {results.length} erfolgreich konvertiert
              </span>
            </CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={handleDownloadAll}
                disabled={successCount === 0}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Alle herunterladen
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {results.map((result, index) => (
                <div
                  key={index}
                  className={cn(
                    'relative rounded-xl overflow-hidden border-2 transition-all duration-200 hover:shadow-lg',
                    result.success
                      ? 'border-neutral-200 dark:border-neutral-700 hover:border-orange-300 dark:hover:border-orange-700'
                      : 'border-red-300 dark:border-red-800'
                  )}
                >
                  {result.success && result.image ? (
                    <>
                      <div className="aspect-square bg-neutral-100 dark:bg-neutral-800">
                        <img
                          src={`data:image/${targetFormat};base64,${result.image}`}
                          alt={result.filename}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-3 space-y-2 bg-white dark:bg-neutral-900">
                        <p className="font-semibold text-sm truncate text-neutral-900 dark:text-neutral-100">
                          {result.filename}
                        </p>
                        <div className="flex items-center justify-between text-xs">
                          <Badge variant="success" className="text-xs">
                            {targetFormat.toUpperCase()}
                          </Badge>
                          <span className="text-neutral-500 dark:text-neutral-400 font-medium">
                            {result.converted_size && formatFileSize(result.converted_size)}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:border-orange-500"
                          onClick={() =>
                            downloadConvertedImage(result.image!, result.filename, targetFormat)
                          }
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="aspect-square flex items-center justify-center bg-red-50 dark:bg-red-950/20 p-4">
                      <p className="text-sm text-red-600 dark:text-red-400 text-center font-medium">
                        {result.error || 'Fehler'}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ConvertMode;
